import React, { useState, useEffect } from "react";
import TextField from "@material-ui/core/TextField";
import { Autocomplete } from "@material-ui/lab";
import Button from "@material-ui/core/Button";
import { CloseRounded } from "@material-ui/icons";
import CheckRounded from "@material-ui/icons/CheckRounded";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import CustomGreenButton from "../../../components/shared/custom-green-button";
import pdf1 from "../../../documents/crear-saml.pdf";
import pdf2 from "../../../documents/guia-azure.pdf";
import pdf3 from "../../../documents/guia-gsuite.pdf";

import { GENERAL_URL_API } from "../../../shared/urls";
import { parseSamlMetadataXml } from "../../../shared/utils/parseSamlMetadataXml";
import swal from "sweetalert";

const createInitialFormData = () =>
  JSON.parse(JSON.stringify(fDataTemplate));

const AgregarNuevoClienteSAMLForm = ({
  idp = null,
  edit = false,
  cancelFn = null,
  refreshIdps = null,
}) => {
  const [formData, setFormData] = useState(createInitialFormData());
  const [loading, setLoading] = useState(false);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState([]);
  const [autoCompleteValue, setAutoCompleteValue] = useState(null);

  useEffect(() => {
    getClientes();
  }, []);

  useEffect(() => {
    if (edit && idp) {
      populateFromIdp(idp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit, idp, autoCompleteOptions]);

  useEffect(() => {
    if (!edit) {
      clearForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit]);

  const getClientes = async () => {
    const fd = new FormData();
    fd.append("show", "all");
    fd.append("tipoUsuario", "Instituto");
    const res = await fetch(`${GENERAL_URL_API}/verDatos/verClientes.php`, {
      method: "POST",
      credentials: "include",
      body: fd,
    });
    const data = await res.json();
    setAutoCompleteOptions(data.data || []);
  };

  const populateFromIdp = (idpData) => {
    const next = createInitialFormData();
    next.samlId.value = idpData.idInstitutoSAML || "";
    next.samlId.disabled = true;
    next.entityID.value = idpData.entityId || idpData.entityID || "";
    next.singleLogoutService.value = idpData.singleLogoutServiceUrl || "";
    next.SingleSignOnService.value = idpData.singleSignOnServiceUrl || "";
    next.x509Cert.value = idpData.x509cert || idpData.x509Cert || "";
    setFormData(next);

    if (idpData.username) {
      const match = autoCompleteOptions.find(
        (opt) => opt.username === idpData.username
      );
      setAutoCompleteValue(match || { username: idpData.username });
    } else {
      setAutoCompleteValue(null);
    }
  };

  const autoCompleteUpdate = (_e, newValue) => {
    setAutoCompleteValue(newValue);
  };

  const applyXmlMetadata = (xmlValue, currentForm = formData) => {
    const parsed = parseSamlMetadataXml(xmlValue);
    return {
      ...currentForm,
      xml: {
        ...currentForm.xml,
        value: xmlValue,
        error: false,
      },
      entityID: {
        ...currentForm.entityID,
        value: parsed.entityID,
        error: false,
      },
      singleLogoutService: {
        ...currentForm.singleLogoutService,
        value: parsed.singleLogoutService,
        error: false,
      },
      SingleSignOnService: {
        ...currentForm.SingleSignOnService,
        value: parsed.singleSignOnService,
        error: false,
      },
      x509Cert: {
        ...currentForm.x509Cert,
        value: parsed.x509Cert,
        error: false,
      },
    };
  };

  const handleChange = (name, value, condition) => {
    if (!(condition === true || condition(value))) return;

    if (name === "xml") {
      setFormData(applyXmlMetadata(value));
      return;
    }

    setFormData({
      ...formData,
      [name]: {
        ...formData[name],
        value,
        error: false,
      },
    });
  };

  const clearForm = () => {
    setFormData(createInitialFormData());
    setAutoCompleteValue(null);
  };

  const handleCancel = () => {
    clearForm();
    if (cancelFn) cancelFn();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = { ...formData };
    let isValid = true;

    for (const formElement in data) {
      if (data[formElement].required && data[formElement].value === "") {
        data[formElement] = { ...data[formElement], error: true };
        isValid = false;
      }
    }
    setFormData(data);

    if (!isValid) return;

    setLoading(true);
    const fd = new FormData();
    fd.append("idInstitutoSAML", formData.samlId.value);
    fd.append(
      "cliente",
      autoCompleteValue === null ? "" : autoCompleteValue.username
    );
    fd.append("entityID", formData.entityID.value);
    fd.append("SingleLogoutServiceUrl", formData.singleLogoutService.value);
    fd.append("SingleSignOnServiceUrl", formData.SingleSignOnService.value);
    fd.append("x509cert", formData.x509Cert.value);
    fd.append("vendedor", localStorage.getItem("userAdminMake"));

    try {
      const res = await fetch(
        `${GENERAL_URL_API}/clientes/crearConexionSAML.php`,
        {
          method: "POST",
          credentials: "include",
          body: fd,
        }
      );
      const resData = await res.json();
      if (resData.status === 1) {
        await swal({
          title: edit ? "IdP actualizado" : "IdP Agregado",
          text: edit
            ? "El IdP se editó satisfactoriamente!"
            : "El IdP se agregó satisfactoriamente!",
          icon: "success",
        });
        if (edit) {
          if (refreshIdps) await refreshIdps();
          handleCancel();
        } else {
          clearForm();
        }
      } else {
        await swal({
          title: "Error",
          text: resData.info,
          icon: "error",
        });
      }
    } catch (error) {
      console.log(error);
      await swal({
        title: "Error",
        text: "No se pudo guardar la conexión SAML",
        icon: "error",
      });
    }
    setLoading(false);
  };

  const textFields = Object.keys(formData).map((key) => {
    const formElement = formData[key];
    let onChangeCondition = true;
    if (formElement.condition !== undefined) {
      onChangeCondition = formElement.condition;
    }
    return (
      <React.Fragment key={key}>
        <label className="admin-label-text mt-3 d-block">
          {formElement.label}
        </label>
        <TextField
          fullWidth
          multiline={!!formElement.multiline}
          rows={formElement.rows || (formElement.multiline ? 4 : 1)}
          maxRows={formElement.maxRows || (formElement.multiline ? 12 : 1)}
          variant="outlined"
          size="small"
          type="text"
          value={formElement.value}
          name={key}
          placeholder={formElement.placeholder || ""}
          disabled={!!formElement.disabled}
          onChange={(e) => {
            const name = e.target.name;
            let value = e.target.value;
            if (formElement.uppercase) value = e.target.value.toUpperCase();
            handleChange(name, value, onChangeCondition);
          }}
          error={!!formElement.error}
          helperText={formElement.error ? "Campo no válido" : null}
        />
      </React.Fragment>
    );
  });

  return (
    <form onSubmit={handleSubmit}>
      {!edit && (
        <ButtonGroup size="small" className="my-3">
          <a className="mr-2" href={pdf1} target="_blank" rel="noreferrer">
            <CustomGreenButton>Manual General</CustomGreenButton>
          </a>
          <a className="mr-2" href={pdf2} target="_blank" rel="noreferrer">
            <CustomGreenButton>Manual Azure</CustomGreenButton>
          </a>
          <a href={pdf3} target="_blank" rel="noreferrer">
            <CustomGreenButton>Manual Gsuite</CustomGreenButton>
          </a>
        </ButtonGroup>
      )}
      <label className="admin-label-text mt-3 d-block">
        Username que ingresará a traves de SAML (Dejar vacio si es Plan Lector)
      </label>
      <Autocomplete
        options={autoCompleteOptions}
        getOptionLabel={(option) => option.username || ""}
        value={autoCompleteValue}
        onChange={autoCompleteUpdate}
        noOptionsText="Sin resultados"
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            style={{
              width: "100%",
              fontFamily: "'Noto Sans JP', sans-serif",
            }}
          />
        )}
      />
      {textFields}
      <div className="mt-3 w-100 d-flex justify-content-end">
        <Button
          type="submit"
          variant="contained"
          color="primary"
          startIcon={<CheckRounded />}
          style={{ background: "#81c868" }}
          disabled={loading}
        >
          {edit ? "Guardar" : "Agregar"}
        </Button>
        <Button
          variant="contained"
          color="primary"
          className="ml-2"
          startIcon={<CloseRounded style={{ fontSize: "25px" }} />}
          style={{ background: "#f05050" }}
          onClick={handleCancel}
          disabled={loading}
        >
          Cancelar
        </Button>
      </div>
    </form>
  );
};

export default AgregarNuevoClienteSAMLForm;

const fDataTemplate = {
  samlId: {
    label: "identificador SAML Instituto (sin espacios)*",
    placeholder: "idSAML (min 4 caracteres y sin espacios)",
    value: "",
    error: false,
    required: true,
    disabled: false,
  },
  xml: {
    label: "XML de metadatos",
    placeholder:
      "Pega aquí el XML de metadatos del IdP para autocompletar entityID, SSO, SLO y certificado",
    multiline: true,
    rows: 8,
    maxRows: 16,
    value: "",
    error: false,
    required: false,
  },
  entityID: {
    label: "entityID",
    placeholder: "Usualmente una URL que se encuentra en el XML del IdP",
    value: "",
    error: false,
    required: false,
  },
  singleLogoutService: {
    label: "SingleLogoutService",
    placeholder:
      "Usualmente una URL que se encuentra en el XML Location de SingleLogoutService",
    value: "",
    error: false,
    required: false,
  },
  SingleSignOnService: {
    label: "SingleSignOnService",
    placeholder:
      "Usualmente una URL que se encuentra en el XML Location de SingleSignOnService",
    value: "",
    error: false,
    required: false,
  },
  x509Cert: {
    label: "x509Cert",
    placeholder: "Usualmente una cadena larga de caracteres",
    multiline: true,
    rows: 4,
    maxRows: 10,
    value: "",
    error: false,
    required: false,
  },
};
